import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Copy, Edit3, Eye, Send, Archive, RotateCcw, GitCompare, Settings, Trash2, Download } from 'lucide-react';
import RHDimensionamentoEscalaEditor from './RHDimensionamentoEscala_Editor';
import RHDimensionamentoEscalaComparar from './RHDimensionamentoEscala_Comparar';
import RHDimensionamentoEscalaCadastros from './RHDimensionamentoEscala_Cadastros';
import RHDimensionamentoEscalaExportar from './RHDimensionamentoEscala_Exportar';
import { btn, sel, aviso, tabela, td, thEscuro, IconeAcao, Girando, useOrdenacao, COR_MODULO } from './escala-ui';

// Dimensionamento de Escala — a lista de versões.
// Espelha o construtor de versões do BSV, com uma diferença que vale saber:
// aqui a versão é POR LOJA, e 'Publicada' é imutável de cara (lá a trava só
// fecha depois do primeiro uso). Ver o cabeçalho do server-dimensionamento-escala.js.
//
// ⚠️ Não confundir com a "Pesquisa de Escala" do RH (a de WhatsApp).

const API = '/api/rh/dimensionamento-escala';

// As 13 lojas da rede. ⚠️ A SED aparece aqui e ainda não tem versão nenhuma:
// é a única que nunca passou pela planilha, e é ela que estreia a ferramenta.
const LOJAS = ['ANV', 'BVV', 'FER', 'GTV', 'MAL', 'MOX', 'PCI', 'PCS', 'RDP', 'SED', 'SVV', 'VEL', 'VSH'];
const TODAS = '__TODAS__';

const statusStyle = (s) => {
    if (s === 'Publicada') return { bg: '#d1fae5', color: '#065f46' };
    if (s === 'Rascunho') return { bg: '#fef3c7', color: '#92400e' };
    return { bg: '#e2e8f0', color: '#475569' };   // Arquivada
};

const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('authToken')}` });
const authJson = () => ({ ...auth(), 'Content-Type': 'application/json' });

const RHDimensionamentoEscala = () => {
    const [loja, setLoja] = useState(TODAS);
    const [versoes, setVersoes] = useState([]);
    const [orcamentos, setOrcamentos] = useState([]);
    const [carregando, setCarregando] = useState(false);
    const [erro, setErro] = useState('');
    const [tela, setTela] = useState({ nome: 'lista' });
    const [selecionadas, setSelecionadas] = useState([]);
    const [ok, setOk] = useState('');
    // ⚠️ QUEM está rodando agora: { id, tipo }. É o que acende o giro no botão
    //    certo e trava a linha inteira enquanto o servidor não responde — ver o
    //    comentário do `Girando` no escala-ui.js para o porquê.
    const [ocupado, setOcupado] = useState(null);

    const todasAsLojas = loja === TODAS;

    const carregar = useCallback(async () => {
        setCarregando(true); setErro('');
        try {
            // Sem `?loja=`, o backend devolve a rede inteira — é o que sustenta
            // a opção "Todas as lojas" (pedido do Rodrigo, 14/09).
            const url = todasAsLojas ? `${API}/versoes` : `${API}/versoes?loja=${encodeURIComponent(loja)}`;
            const [rv, ro] = await Promise.all([
                fetch(url, { headers: auth() }),
                fetch(`${API}/orcamento`, { headers: auth() }),
            ]);
            if (!rv.ok) throw new Error(`HTTP ${rv.status}`);
            setVersoes(await rv.json());
            setOrcamentos(ro.ok ? await ro.json() : []);
        } catch (e) {
            setErro('Erro ao carregar as versões. Se o módulo é novo, confira se o schema foi rodado no banco.');
        } finally {
            setCarregando(false);
        }
    }, [loja, todasAsLojas]);

    useEffect(() => { carregar(); setSelecionadas([]); }, [carregar]);

    // O orçamento vigente de cada loja é o do ano mais recente cadastrado.
    const orcamentoDe = (sigla) => orcamentos
        .filter((o) => o.LOJA === sigla)
        .sort((a, b) => b.ANO - a.ANO)[0] || null;

    // ⚠️ A lista ficou SÓ com identidade (decisão do Rodrigo, 14/09): horas,
    //    posições, efetivo, orçado, diferença e avisos saíram. Eles são números
    //    DA ESCALA, e o lugar deles é dentro da versão — na lista viravam uma
    //    parede de dígitos que não ajuda a achar a versão que se procura.
    const { linhas: ordenadas, cabecalho, Seta } = useOrdenacao(versoes, todasAsLojas ? 'LOJA' : 'NOME_VERSAO');

    // Toda ação que vai ao servidor passa por aqui — e é aqui que mora a regra
    // de feedback: trava o botão, mostra o giro, e ao terminar DIZ o que fez.
    // ⚠️ `sucesso` é o texto do aviso verde. Sem ele, uma exclusão bem-sucedida
    //    é indistinguível de um clique que não pegou: a linha simplesmente some
    //    da tabela alguns segundos depois, e quem clicou não sabe se foi ele.
    const acao = async (url, metodo, corpo, confirmacao, alvo, sucesso) => {
        if (confirmacao && !window.confirm(confirmacao)) return null;
        setOcupado(alvo || { id: 0, tipo: 'geral' });
        setErro(''); setOk('');
        try {
            const r = await fetch(url, {
                method: metodo,
                headers: corpo ? authJson() : auth(),
                body: corpo ? JSON.stringify(corpo) : undefined,
            });
            const data = await r.json().catch(() => ({}));
            if (!r.ok) { setErro(data?.message || 'Não foi possível concluir a ação.'); return null; }
            // ⚠️ O `carregar()` é AGUARDADO antes de soltar o botão: soltar
            //    antes deixaria a tabela velha na tela com os botões já ativos,
            //    que é convite para agir sobre uma linha que não existe mais.
            await carregar();
            if (sucesso) setOk(sucesso);
            return data;
        } catch (e) {
            setErro('A ação não chegou ao servidor. Confira a conexão e tente de novo.');
            return null;
        } finally {
            setOcupado(null);
        }
    };

    // "esta linha está ocupada com esta ação?" — o que acende o giro certo.
    const rodando = (id, tipo) => ocupado?.id === id && ocupado?.tipo === tipo;

    // ⚠️ O NOME NÃO É PEDIDO — o servidor gera no padrão LOJA-ANO-vNN.
    //    Rodrigo, 14/09: "não quero que a versão seja um nome SOLTO que qualquer
    //    um escreve o que quiser". Nome livre parece inofensivo e não é: em seis
    //    meses a lista teria "escala nova 2", "TESTE", "ideal final AGORA", e a
    //    pergunta que o módulo existe para responder vira arqueologia.
    const novaVersao = async () => {
        const alvo = todasAsLojas ? window.prompt(`Para qual loja? (${LOJAS.join(', ')})`, 'SED') : loja;
        if (!alvo) return;
        const sigla = String(alvo).toUpperCase().trim();
        if (!LOJAS.includes(sigla)) { setErro(`Loja "${alvo}" não existe na rede.`); return; }
        const d = await acao(`${API}/versoes`, 'POST', { loja: sigla },
            null, { id: 0, tipo: 'nova' }, null);   // a mensagem seria engolida: abre o editor em seguida
        // Versão recém-criada nasce vazia — abrir para olhar não faria sentido.
        if (d?.id) setTela({ nome: 'editor', id: d.id, modo: 'edicao' });
    };

    const duplicar = async (v) => {
        if (!window.confirm(`Duplicar ${v.NOME_VERSAO}? A cópia nasce como Rascunho, com o próximo número da ${v.LOJA}.`)) return;
        const d = await acao(`${API}/versoes/${v.ID}/duplicar`, 'POST', {},
            null, { id: v.ID, tipo: 'duplicar' }, null);   // idem: cai direto no editor da cópia
        if (d?.id) setTela({ nome: 'editor', id: d.id, modo: 'edicao' });
    };

    // ⚠️ Exclui QUALQUER status, inclusive Publicada (decisão do Rodrigo,
    //    14/09: "pode liberar pra poder excluir qualquer uma por enquanto,
    //    depois eu travo isso caso seja necessário"). Vai contra o princípio do
    //    módulo — versão publicada é registro datado — e é consciente: a
    //    ferramenta está em estreia e a lista precisa poder ser limpa.
    const excluir = (v) => acao(
        `${API}/versoes/${v.ID}`, 'DELETE', null,
        `Excluir ${v.NOME_VERSAO}? A escala e todas as linhas dela somem, e isso não se desfaz.`,
        { id: v.ID, tipo: 'excluir' },
        `${v.LOJA} · ${v.NOME_VERSAO} foi excluída.`
    );

    const alternarSelecao = (id) => setSelecionadas((s) =>
        s.includes(id) ? s.filter((x) => x !== id) : [...s, id].slice(-2));

    // Comparar só faz sentido dentro da mesma loja — o backend recusa o resto.
    const selecionadasDaMesmaLoja = selecionadas.length === 2 &&
        new Set(selecionadas.map((id) => versoes.find((v) => v.ID === id)?.LOJA)).size === 1;

    if (tela.nome === 'editor') {
        return <RHDimensionamentoEscalaEditor id={tela.id}
            modoInicial={tela.modo || 'visualizacao'}
            onSair={() => { setTela({ nome: 'lista' }); carregar(); }}
            // ⚠️ Navegar entre lojas/versões SEM voltar à lista: o editor pede
            //    a troca e quem manda no id continua sendo esta tela.
            //    ⚠️ A versão nova abre em VISUALIZAÇÃO — o modo de edição valia
            //    para a versão em que foi ligado, não para a próxima.
            onTrocarVersao={(novoId) => setTela({ nome: 'editor', id: novoId, modo: 'visualizacao' })} />;
    }
    if (tela.nome === 'comparar') {
        return <RHDimensionamentoEscalaComparar a={tela.a} b={tela.b} onSair={() => setTela({ nome: 'lista' })} />;
    }
    if (tela.nome === 'cadastros') {
        return <RHDimensionamentoEscalaCadastros onSair={() => { setTela({ nome: 'lista' }); carregar(); }} />;
    }
    if (tela.nome === 'exportar') {
        return <RHDimensionamentoEscalaExportar onSair={() => setTela({ nome: 'lista' })} />;
    }

    const orcamentoLoja = todasAsLojas ? null : orcamentoDe(loja);

    return (
        <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
                <label style={{ fontWeight: 700, fontSize: 13, color: '#475569' }}>Loja</label>
                <select value={loja} onChange={(e) => setLoja(e.target.value)} style={sel}>
                    <option value={TODAS}>Todas as lojas</option>
                    {LOJAS.map((x) => <option key={x} value={x}>{x}</option>)}
                </select>
                <button onClick={novaVersao} disabled={!!ocupado} style={btn(ocupado ? '#e2e8f0' : COR_MODULO, ocupado ? '#94a3b8' : '#fff')}>
                    {rodando(0, 'nova') ? <Girando size={14} /> : <Plus size={14} />} Nova versão
                </button>
                <button
                    onClick={() => setTela({ nome: 'comparar', a: selecionadas[0], b: selecionadas[1] })}
                    disabled={!selecionadasDaMesmaLoja}
                    title={selecionadasDaMesmaLoja ? 'Comparar as duas versões marcadas'
                        : 'Marque DUAS versões da MESMA loja para comparar'}
                    style={{ ...btn('#fff', selecionadasDaMesmaLoja ? COR_MODULO : '#cbd5e1'), border: '1px solid #e2e8f0' }}
                >
                    <GitCompare size={14} /> Comparar
                    {selecionadas.length === 2 && !selecionadasDaMesmaLoja ? ' (mesma loja)' : selecionadas.length === 2 ? '' : ' (marque 2)'}
                </button>
                <div style={{ flex: 1 }} />
                {/* ⚠️ TELA PRÓPRIA, e não um botão dentro da versão (Rodrigo,
                    15/09): exportar uma escala por vez não é o uso real — o
                    arquivo que ele monta é da REDE, com várias lojas juntas. */}
                <button onClick={() => setTela({ nome: 'exportar' })} title="Tabela no formato da planilha e a escala gráfica em PDF"
                    style={{ ...btn('#fff', '#475569'), border: '1px solid #e2e8f0' }}>
                    <Download size={14} /> Exportar
                </button>
                <button onClick={() => setTela({ nome: 'cadastros' })} title="Turnos da rede, cargos da grade e orçamento por loja"
                    style={{ ...btn('#fff', '#475569'), border: '1px solid #e2e8f0' }}>
                    <Settings size={14} /> Turnos, cargos e orçamento
                </button>
            </div>

            {orcamentoLoja && (
                <div style={aviso('#fef3c7', '#92400e')}>
                    Orçamento de quadro da <b>{loja}</b> em <b>{orcamentoLoja.ANO}</b>: <b>{orcamentoLoja.QTD_ORCADA}</b> pessoas
                    {orcamentoLoja.COMPOSICAO ? ` (${orcamentoLoja.COMPOSICAO})` : ''}.{' '}
                    É contra ele que o <b>efetivo</b> de cada versão é lido — nunca contra as posições.
                </div>
            )}
            {erro && <div style={aviso('#fee2e2', '#991b1b')}>{erro}</div>}
            {/* ⚠️ A confirmação do que ACONTECEU. Uma exclusão sem isto é
                indistinguível de um clique que não pegou — a linha só some da
                tabela alguns segundos depois. (Rodrigo, 15/09.) */}
            {ok && <div style={aviso('#dcfce7', '#166534')}>{ok}</div>}

            {carregando ? (
                <p style={{ color: '#64748b' }}>Carregando…</p>
            ) : ordenadas.length === 0 ? (
                <div style={aviso('#f1f5f9', '#475569')}>
                    {todasAsLojas
                        ? 'Nenhuma versão de escala cadastrada na rede. Se o seed ainda não foi rodado no banco, é isso.'
                        : <>A <b>{loja}</b> ainda não tem nenhuma versão de escala.
                            {loja === 'SED' && ' — e isso é esperado: a SED é a única loja da rede que nunca passou pela planilha, e é ela que vai estrear a ferramenta.'}</>}
                </div>
            ) : (
                <div style={{
                    overflowX: 'auto', border: '1px solid #e2e8f0', borderRadius: 10,
                    // ⚠️ A tabela inteira esmaece e para de aceitar clique enquanto
                    //    uma ação roda: é o sinal que se vê sem precisar reparar no
                    //    botão que foi clicado, e impede agir em OUTRA linha no meio.
                    opacity: ocupado ? 0.55 : 1,
                    pointerEvents: ocupado ? 'none' : 'auto',
                    transition: 'opacity .15s',
                }}>
                    <table style={{ ...tabela, borderRadius: 10, overflow: 'hidden' }}>
                        <thead>
                            {/* ⚠️ Cabeçalho ESCURO com letra branca (pedido do Rodrigo, 14/09).
                                O cinza sobre branco do padrão antigo sumia na tela. */}
                            <tr style={{ background: '#0f172a' }}>
                                <th style={thEscuro}></th>
                                {todasAsLojas && <th {...cabecalho('LOJA', thEscuro)}>Loja <Seta campo="LOJA" /></th>}
                                <th {...cabecalho('NOME_VERSAO', thEscuro)}>Versão <Seta campo="NOME_VERSAO" /></th>
                                <th {...cabecalho('STATUS', thEscuro)}>Status <Seta campo="STATUS" /></th>
                                <th {...cabecalho('USUARIO_CRIACAO', thEscuro)}>Criada por <Seta campo="USUARIO_CRIACAO" /></th>
                                <th {...cabecalho('DATA_CRIACAO', thEscuro)}>Data criação <Seta campo="DATA_CRIACAO" /></th>
                                <th style={thEscuro}>Ações</th>
                            </tr>
                        </thead>
                        <tbody>
                            {ordenadas.map((v) => {
                                const st = statusStyle(v.STATUS);
                                return (
                                    <tr key={v.ID}>
                                        <td style={td}>
                                            <input type="checkbox" checked={selecionadas.includes(v.ID)}
                                                onChange={() => alternarSelecao(v.ID)}
                                                title="Marque duas versões da mesma loja para comparar" />
                                        </td>
                                        {todasAsLojas && <td style={{ ...td, fontWeight: 800 }}>{v.LOJA}</td>}
                                        {/* Sem a DESCRICAO embaixo: ela engordava a linha e não
                                            informava nada que o nome padronizado já não diga. */}
                                        <td style={{ ...td, fontWeight: 700, letterSpacing: 0.3 }}>{v.NOME_VERSAO}</td>
                                        <td style={td}>
                                            <span style={{ background: st.bg, color: st.color, padding: '3px 10px', borderRadius: 999, fontSize: 11, fontWeight: 700 }}>
                                                {v.STATUS}
                                            </span>
                                        </td>
                                        <td style={td}>{v.USUARIO_CRIACAO || '—'}</td>
                                        <td style={td}>{new Date(v.DATA_CRIACAO).toLocaleDateString('pt-BR')}</td>
                                        {/* Só ícones, cor sugestiva, função no hover — padrão do Painel Admin */}
                                        <td style={{ ...td, whiteSpace: 'nowrap' }}>
                                            {/* ⚠️ DOIS caminhos, de propósito (Rodrigo, 15/09): o olho
                                                ABRE PARA OLHAR — lá dentro há um botão Editar — e o
                                                lápis entra direto no modo de edição. Antes o olho já
                                                entregava a tela editável, e um clique torto mudava a
                                                escala de quem só queria conferir. */}
                                            <IconeAcao onClick={() => setTela({ nome: 'editor', id: v.ID, modo: 'visualizacao' })}
                                                cor="#2563eb" titulo="Ver a escala (só leitura; dá para editar lá dentro)">
                                                <Eye size={17} />
                                            </IconeAcao>
                                            <IconeAcao onClick={() => setTela({ nome: 'editor', id: v.ID, modo: 'edicao' })}
                                                cor="#7c3aed" desabilitado={v.STATUS === 'Arquivada'}
                                                titulo={v.STATUS === 'Arquivada'
                                                    ? 'Versão arquivada não se edita — republique-a antes'
                                                    : 'Abrir já no modo de edição'}>
                                                <Edit3 size={17} />
                                            </IconeAcao>
                                            <IconeAcao onClick={() => duplicar(v)} cor="#0891b2"
                                                ocupado={rodando(v.ID, 'duplicar')}
                                                titulo="Duplicar como novo Rascunho — é o caminho para alterar uma versão publicada">
                                                <Copy size={17} />
                                            </IconeAcao>
                                            <IconeAcao onClick={() => acao(`${API}/versoes/${v.ID}/publicar`, 'POST', null,
                                                'Publicar esta versão? Ela vira o registro datado da escala e não poderá mais ser editada — para mudar, duplique.',
                                                { id: v.ID, tipo: 'publicar' }, `${v.LOJA} · ${v.NOME_VERSAO} publicada.`)}
                                                ocupado={rodando(v.ID, 'publicar')}
                                                cor="#16a34a" desabilitado={v.STATUS !== 'Rascunho'}
                                                titulo={v.STATUS === 'Rascunho' ? 'Publicar — congela como registro datado' : 'Só um Rascunho pode ser publicado'}>
                                                <Send size={17} />
                                            </IconeAcao>
                                            <IconeAcao onClick={() => acao(`${API}/versoes/${v.ID}/arquivar`, 'POST', null, 'Arquivar esta versão?',
                                                { id: v.ID, tipo: 'arquivar' }, `${v.LOJA} · ${v.NOME_VERSAO} arquivada.`)}
                                                ocupado={rodando(v.ID, 'arquivar')}
                                                cor="#64748b" desabilitado={v.STATUS !== 'Publicada'}
                                                titulo={v.STATUS === 'Publicada' ? 'Arquivar — tira da circulação sem perder o registro' : 'Só uma versão Publicada pode ser arquivada'}>
                                                <Archive size={17} />
                                            </IconeAcao>
                                            {v.STATUS === 'Arquivada' && (
                                                <IconeAcao onClick={() => acao(`${API}/versoes/${v.ID}/republicar`, 'POST', null, 'Republicar esta versão?',
                                                    { id: v.ID, tipo: 'republicar' }, `${v.LOJA} · ${v.NOME_VERSAO} voltou a valer.`)}
                                                    ocupado={rodando(v.ID, 'republicar')}
                                                    cor="#7c3aed" titulo="Republicar — volta a valer">
                                                    <RotateCcw size={17} />
                                                </IconeAcao>
                                            )}
                                            <IconeAcao onClick={() => excluir(v)} cor="#e11d48"
                                                ocupado={rodando(v.ID, 'excluir')}
                                                titulo="Excluir definitivamente esta versão e todas as suas linhas">
                                                <Trash2 size={17} />
                                            </IconeAcao>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            <p style={{ fontSize: 11, color: '#94a3b8', marginTop: 10 }}>
                Clique no cabeçalho para ordenar. O nome da versão é gerado no padrão
                <b> LOJA-ANO-vNN</b> — os números da escala (horas, posições, efetivo e o confronto com o
                orçado) ficam dentro da versão.
            </p>
        </div>
    );
};

export default RHDimensionamentoEscala;
